import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { BASE_URL } from "../../API/webserviceUrl";

// ── Axios instance ──────────────────────────────────────────
// Base URL corrected to a valid URL (added the missing "//").
// const api = axios.create({
//   baseURL: "http://localhost:8989",
// });

interface GroupMember {
  id: string;
  name: string;
}

interface Group {
  id: string;
  name: string;
  members: GroupMember[];
  leadId: string | null;
  confirmed: boolean;
}

// Pool item — a student available to be picked into a new group.
// `isAssigned` reflects the API's `is_assigned` (already in some
// group) and is used ONLY to disable/flag the option, never to
// drive the checkbox's checked state.
interface PoolMember extends GroupMember {
  isAssigned: boolean;
}

// ── API response shapes ─────────────────────────────────────
interface StudentApiItem {
  student_id: string;
  name: string;
  is_assigned: boolean;
}

interface StudentListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: StudentApiItem[];
}

interface GroupApiMember {
  student_id: string;
  name: string;
  is_lead: boolean;
}

interface GroupApiItem {
  group_number: number;
  group_id: number;
  problem_stmt: string | null;
  format_type: string | null;
  ppt_file_path: string | null;
  status: number;
  created_on: string;
  members: GroupApiMember[];
}

interface GroupsListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: GroupApiItem[];
}

interface CreateGroupResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: unknown;
}

function mapApiGroup(g: GroupApiItem): Group {
  const leadMember = g.members.find((m) => m.is_lead);
  return {
    id: String(g.group_id),
    name: `Group ${g.group_id}`,
    members: g.members.map((m) => ({ id: m.student_id, name: m.name })),
    leadId: leadMember ? leadMember.student_id : null,
    confirmed: Boolean(leadMember),
  };
}

export default function TopGroupFormation() {
  // Pool is the full list of students fetched from /student-list — users
  // are NEVER removed from it, so the same person can be selected into
  // multiple groups. `isAssigned` is display-only metadata; it does NOT
  // control checked state (selectedUserIds is the single source of truth
  // for that).
  const [pool, setPool] = useState<PoolMember[]>([]);
  const [poolLoading, setPoolLoading] = useState(true);
  const [poolError, setPoolError] = useState<string | null>(null);

  const [groups, setGroups] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Which group's member-edit dropdown is currently open (only one at a time)
  const [openGroupEditId, setOpenGroupEditId] = useState<string | null>(null);

  // /edit-group — saving state + per-group error while adding/removing members
  const [memberSavingId, setMemberSavingId] = useState<string | null>(null);
  const [memberEditErrors, setMemberEditErrors] = useState<any>({});

  // /delete-group — saving state + per-group error while deleting
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({});

  // /make-group — saving state + per-group error while setting the lead
  const [leadSavingId, setLeadSavingId] = useState<string | null>(null);
  const [leadErrors, setLeadErrors] = useState<Record<string, string>>({});

  const memberDropdownRef = useRef<HTMLDivElement>(null);
  const groupEditRefs = useRef<Record<string, HTMLDivElement | null>>({});
  let token = localStorage.getItem("token");

  // ── API calls ────────────────────────────────────────────
  async function fetchStudents() {
    setPoolLoading(true);
    setPoolError(null);
    try {
      const res = await axios.get(
        `${BASE_URL}/student-list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const list: StudentApiItem[] = res.data?.data ?? [];
      setPool(
        list.map((s) => ({
          id: s.student_id,
          name: s.name,
          isAssigned: Boolean(s.is_assigned),
        })),
      );
    } catch (err) {
      console.error("Failed to fetch student list", err);
      setPoolError("Couldn't load the student list.");
    } finally {
      setPoolLoading(false);
    }
  }

  async function fetchGroups() {
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const res = await axios.get(
        `${BASE_URL}/get-all-groups`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const list = res.data?.data ?? [];
      setGroups(list.map(mapApiGroup));
    } catch (err) {
      console.error("Failed to fetch groups", err);
      setGroupsError("Couldn't load groups.");
    } finally {
      setGroupsLoading(false);
    }
  }

  useEffect(() => {
    fetchStudents();
    fetchGroups();
  }, []);

  // close member dropdown(s) on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;

      if (
        memberDropdownRef.current &&
        !memberDropdownRef.current.contains(target)
      ) {
        setMemberDropdownOpen(false);
      }

      if (openGroupEditId) {
        const activeRef = groupEditRefs.current[openGroupEditId];
        if (activeRef && !activeRef.contains(target)) {
          setOpenGroupEditId(null);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openGroupEditId]);

  function initials(name: string) {
    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("");
  }

  // Toggles a student in/out of selectedUserIds. Students already
  // assigned to a group (isAssigned) are blocked here so a stray
  // click can't select them.
  function toggleUserSelection(id: string) {
    const student = pool.find((u) => u.id === id);
    if (student?.isAssigned) return;

    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  async function handleCreateGroup(e: React.FormEvent) {
    e.preventDefault();
    if (selectedUserIds.length === 0 || creating) return;

    setCreating(true);
    setCreateError(null);
    try {
      await axios.post(
        `${BASE_URL}/create-group`,
        {
          members: selectedUserIds,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      // The backend assigns group_number/group_id, so re-fetch the
      // canonical list rather than constructing the group locally.
      await fetchGroups();
      // Re-fetch students too — the ones just grouped are now
      // is_assigned: true on the backend.
      await fetchStudents();
      setSelectedUserIds([]);
      setMemberDropdownOpen(false);
    } catch (err) {
      console.error("Failed to create group", err);
      setCreateError("Couldn't create the group. Please try again.");
    } finally {
      setCreating(false);
    }
  }

  // /make-group — sets a group's lead.
  async function setLead(groupId: string, userId: string) {
    if (leadSavingId === groupId) return;

    setLeadSavingId(groupId);
    setLeadErrors((prev) => ({ ...prev, [groupId]: "" }));
    try {
      await axios.post(
        `${BASE_URL}/make-group`,
        {
          group_id: groupId,
          student_id: userId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setGroups((prev) =>
        prev.map((g) => (g.id === groupId ? { ...g, leadId: userId } : g)),
      );
    } catch (err) {
      console.error("Failed to set group lead", err);
      setLeadErrors((prev) => ({
        ...prev,
        [groupId]: "Couldn't set the lead. Please try again.",
      }));
    } finally {
      setLeadSavingId(null);
    }
  }

  function confirmGroup(groupId: string) {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId && g.leadId ? { ...g, confirmed: true } : g,
      ),
    );
  }

  function editGroup(groupId: string) {
    setGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, confirmed: false } : g)),
    );
  }

  // /delete-group — removes a group.
  async function deleteGroup(groupId: string) {
    if (deletingId === groupId) return;

    setDeletingId(groupId);
    setDeleteErrors((prev) => ({ ...prev, [groupId]: "" }));
    try {
      await axios.delete(
        `${BASE_URL}/delete-group`,
        {
          data: {
            group_id: Number(groupId),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
      setOpenGroupEditId((prev) => (prev === groupId ? null : prev));
      // Deleting a group frees up its members — refresh the pool so
      // they're selectable again.
      await fetchStudents();
    } catch (err) {
      console.error("Failed to delete group", err);
      setDeleteErrors((prev) => ({
        ...prev,
        [groupId]: "Couldn't delete this group. Please try again.",
      }));
    } finally {
      setDeletingId(null);
    }
  }

  // /edit-group — adds or removes a member from a specific group by sending
  // the full updated member list. Members can be edited fully — added,
  // removed, changed — at any time while the group is pending (not yet
  // confirmed). If the removed member was the lead, the lead selection is
  // cleared locally (the lead itself is only changed via /make-group).
  async function toggleGroupMember(groupId: string, userId: string) {
    if (memberSavingId === groupId) return;

    const group = groups.find((g) => g.id === groupId);
    if (!group) return;

    const isMember = group.members.some((m) => m.id === userId);
    let newMembers: GroupMember[];

    if (isMember) {
      newMembers = group.members.filter((m) => m.id !== userId);
    } else {
      const user = pool.find((u) => u.id === userId);
      if (!user) return;
      newMembers = [...group.members, { id: user.id, name: user.name }];
    }

    setMemberSavingId(groupId);
    setMemberEditErrors((prev: any) => ({ ...prev, [groupId]: "" }));
    try {
      await axios.put(
        `${BASE_URL}/edit-group`,
        {
          group_id: Number(groupId),
          members: newMembers.map((m) => m.id),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setGroups((prev) =>
        prev.map((g) => {
          if (g.id !== groupId) return g;
          const newLeadId = newMembers.some((m) => m.id === g.leadId)
            ? g.leadId
            : null;
          return { ...g, members: newMembers, leadId: newLeadId };
        }),
      );
      // Membership changed on the backend — refresh the pool's
      // is_assigned flags so they stay accurate.
      await fetchStudents();
    } catch (err) {
      console.error("Failed to edit group members", err);
      setMemberEditErrors((prev: any) => ({
        ...prev,
        [groupId]: "Couldn't update members. Please try again.",
      }));
    } finally {
      setMemberSavingId(null);
    }
  }

  const selectedUsers = pool.filter((u) => selectedUserIds.includes(u.id));

  return (
    <div className="page-wrapper">
      <div className="content mb-3">
        <style>{`* {
  box-sizing: border-box;
}

.top-root {
  container-type: inline-size;
  container-name: top;
  width: 100%;
  min-width: 0;
}

.top-page {
  margin: 0;
  font-family: "Inter", "Segoe UI", Arial, sans-serif;
  color: #1a1a1a;
  min-width: 0;
}

.top-title {
  margin: 0 0 2px;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.top-subtitle {
  margin: 0 0 14px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.45;
}

/* ── Create Group Card ─────────────────────────────────────── */
.top-form-card {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04),
    0 8px 24px rgba(16, 24, 40, 0.04);
}

.top-form-title {
  margin: 0 0 10px;
  font-size: 14.5px;
  font-weight: 800;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.top-form-title-next {
  font-weight: 500;
  color: #9ca3af;
  font-size: 12px;
  background: #f7f7f5;
  border-radius: 999px;
  padding: 2px 9px;
}

.top-field {
  min-width: 0;
}

.top-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #4b5563;
  margin-bottom: 5px;
  letter-spacing: 0.01em;
}

/* ── Member multi-select dropdown ───────────────────────────── */
.top-multiselect {
  position: relative;
  min-width: 0;
}

.top-multiselect-trigger {
  width: 100%;
  min-height: 42px;
  border: 1.5px solid #e6e3d9;
  border-radius: 5px;
  padding: 6px 34px 6px 9px;
  background: #fbfbfa;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  position: relative;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  min-width: 0;
}

.top-multiselect-trigger:hover {
  border-color: #16a34a;
  background: #fff;
}

.top-multiselect-trigger.open {
  border-color: #16a34a;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(22, 163, 74, 0.1);
}

.top-multiselect-trigger.disabled {
  cursor: default;
  background: #fafafa;
  opacity: 0.75;
}

.top-multiselect-placeholder {
  font-size: 13px;
  color: #9ca3af;
  padding: 3px 3px;
}

.top-multiselect-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #eefaf1;
  border: 1px solid #bfe8cf;
  color: #128a3e;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  padding: 3px 5px 3px 9px;
  max-width: 100%;
}

.top-multiselect-chip button {
  border: none;
  background: rgba(18, 138, 62, 0.12);
  color: #128a3e;
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  padding: 3px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  transition: background 0.15s;
}

.top-multiselect-chip button:hover {
  background: rgba(18, 138, 62, 0.22);
}

.top-multiselect-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%) rotate(0deg);
  pointer-events: none;
  color: #9ca3af;
  transition: transform 0.2s;
}

.top-multiselect-trigger.open .top-multiselect-arrow {
  transform: translateY(-50%) rotate(180deg);
  color: #16a34a;
}

.top-multiselect-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(16, 24, 40, 0.04),
    0 16px 36px rgba(16, 24, 40, 0.14);
  padding: 6px;
  max-height: 264px;
  overflow-y: auto;
  z-index: 30;
  animation: top-panel-in 0.12s ease-out;
}

@keyframes top-panel-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.top-multiselect-option {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 9px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.12s;
}

.top-multiselect-option:hover {
  background: #f6f8f5;
}

.top-multiselect-option.selected {
  background: #eefaf1;
  color: #128a3e;
}

.top-multiselect-option.assigned {
  cursor: not-allowed;
  opacity: 0.55;
}

.top-multiselect-option.assigned:hover {
  background: transparent;
}

.top-multiselect-option input {
  accent-color: #16a34a;
  flex-shrink: 0;
  width: 15px;
  height: 15px;
}

.top-option-tag {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 9.5px;
  font-weight: 800;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 2px 7px;
  border-radius: 999px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.top-submit-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.top-btn {
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  padding: 9px 18px;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s, transform 0.1s,
    box-shadow 0.15s;
}

.top-btn-primary {
  background: #16a34a;
  color: #fff;
  box-shadow: 0 1px 2px rgba(22, 163, 74, 0.15),
    0 4px 10px rgba(22, 163, 74, 0.18);
}

.top-btn-primary:hover:not(:disabled) {
  background: #128a3e;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(22, 163, 74, 0.18),
    0 8px 16px rgba(22, 163, 74, 0.22);
}

.top-btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.top-btn-primary:disabled {
  background: #d7ecdf;
  color: #fff;
  box-shadow: none;
  cursor: not-allowed;
}

.top-btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.top-btn-secondary:hover {
  background: #e5e7eb;
}

/* ── Groups grid ───────────────────────────────────────────── */
.top-groups-heading {
  font-size: 14.5px;
  font-weight: 600;
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.top-groups-count-pill {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 2px 8px;
}

.top-groups {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 10px;
}

.top-group-card {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 12px;
  min-width: 0;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.03),
    0 6px 16px rgba(16, 24, 40, 0.035);
  transition: box-shadow 0.18s, transform 0.18s, border-color 0.18s;
  min-height: 360px;
  display: flex;
  flex-direction: column;
}

.top-group-card:hover {
  box-shadow: 0 2px 4px rgba(16, 24, 40, 0.05),
    0 14px 28px rgba(16, 24, 40, 0.08);
  transform: translateY(-2px);
  border-color: #e2e2e2;
}

.top-group-card.is-confirmed {
  border-color: #cfe9dc;
  background: linear-gradient(180deg, #fbfffc 0%, #ffffff 60%);
}

.top-group-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 3px;
  flex-shrink: 0;
}

.top-group-head h3 {
  margin: 0;
  font-size: 14.5px;
  font-weight: 800;
  overflow-wrap: anywhere;
  min-width: 0;
}

.top-status-badge {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.top-status-confirmed {
  background: #e7f5ee;
  color: #16a34a;
}

.top-status-pending {
  background: #fef3e2;
  color: #b45309;
}

.top-group-count {
  font-size: 11px;
  color: #9ca3af;
  margin: 2px 0 8px;
  font-weight: 600;
  flex-shrink: 0;
}

/* ── Per-group member editor ─────────────────────────────────── */
.top-group-multiselect {
  position: relative;
  flex-shrink: 0;
  margin-bottom: 10px;
}

.top-group-multiselect .top-label {
  font-size: 10.5px;
  margin-bottom: 4px;
}

.top-group-multiselect .top-multiselect-trigger {
  min-height: 34px;
  padding: 4px 30px 4px 6px;
}

.top-group-multiselect .top-multiselect-chip {
  font-size: 11px;
  padding: 2px 4px 2px 8px;
}

.top-group-multiselect .top-multiselect-chip button {
  width: 14px;
  height: 14px;
  font-size: 10px;
}

.top-group-multiselect .top-multiselect-panel {
  max-height: 200px;
}

.top-lead-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}

.top-lead-list::-webkit-scrollbar {
  width: 6px;
}

.top-lead-list::-webkit-scrollbar-thumb {
  background: #d9d9d3;
  border-radius: 999px;
}

.top-lead-list::-webkit-scrollbar-track {
  background: transparent;
}

.top-lead-empty {
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  padding: 14px 6px;
  border: 1.5px dashed #eee;
  border-radius: 10px;
}

.top-lead-option {
  display: flex;
  align-items: center;
  gap: 9px;
  border: 1.5px solid #eee;
  border-radius: 10px;
  padding: 7px 9px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  background: #fff;
}

.top-lead-option:hover {
  border-color: #16a34a;
}

.top-lead-option.selected {
  background: #eefaf1;
  border-color: #16a34a;
}

.top-lead-option input {
  accent-color: #16a34a;
  flex-shrink: 0;
  width: 15px;
  height: 15px;
}

.top-chip-avatar {
  flex-shrink: 0;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background: #e7f5ee;
  color: #16a34a;
  font-size: 10px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.top-lead-name {
  font-size: 12.5px;
  font-weight: 600;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.top-lead-tag {
  flex-shrink: 0;
  font-size: 9.5px;
  font-weight: 800;
  color: #16a34a;
  background: #d9f2e3;
  padding: 3px 8px;
  border-radius: 999px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.top-group-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-top: 3px;
  border-top: 1px dashed #f0f0ee;
  flex-shrink: 0;
}

.top-group-footer-right {
  display: flex;
  gap: 8px;
}

.top-btn-danger {
  background: #fef2f2;
  color: #b91c1c;
}

.top-btn-danger:hover {
  background: #fee2e2;
}

.top-btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.top-empty-state {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
  padding: 20px 0;
  border: 1.5px dashed #e6e3d9;
  border-radius: 14px;
  background: #fbfbfa;
}

/* ── Inline loading / error notes ────────────────────────────── */
.top-inline-note {
  font-size: 12px;
  color: #9ca3af;
  margin: 6px 0 0;
}

.top-inline-error {
  font-size: 12px;
  color: #b91c1c;
  margin: 6px 0 0;
}

.top-inline-link-btn {
  border: none;
  background: none;
  color: #16a34a;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: 12px;
}

/* ── Container queries — responsive ───────────────────────── */
@container top (max-width: 640px) {
  .top-groups {
    grid-template-columns: 1fr;
  }
}

@container top (max-width: 420px) {
  .top-page {
    padding: 10px 10px 18px;
  }
  .top-submit-row {
    justify-content: stretch;
  }
  .top-submit-row .top-btn {
    width: 100%;
  }
}

@supports not (container-type: inline-size) {
  @media (max-width: 640px) {
    .top-groups {
      grid-template-columns: 1fr;
    }
  }
}`}</style>

        <div className="top-root">
          <div className="top-page">
            <h1 className="top-title">TOP Group Formation</h1>
            <p className="top-subtitle">
              Select members and create a group — group numbers are assigned
              automatically. Students already in a group are shown greyed out
              and can't be re-selected. You can add or remove members from a
              group at any time while it's pending, then pick a lead and
              confirm.
            </p>

            {/* ── Create Group Form ─────────────────────────── */}
            <form className="top-form-card" onSubmit={handleCreateGroup}>
              <h2 className="top-form-title">
                Create group
                <span className="top-form-title-next">
                  will be named "Group {groups.length + 1}"
                </span>
              </h2>

              {/* Members multi-select dropdown */}
              <div className="top-field" ref={memberDropdownRef}>
                <label className="top-label">Members</label>
                <div className="top-multiselect">
                  <div
                    className={`top-multiselect-trigger ${
                      memberDropdownOpen ? "open" : ""
                    } ${poolLoading ? "disabled" : ""}`}
                    onClick={() => {
                      if (poolLoading) return;
                      setMemberDropdownOpen((prev) => !prev);
                    }}
                  >
                    {selectedUsers.length === 0 ? (
                      <span className="top-multiselect-placeholder">
                        {poolLoading ? "Loading students…" : "Select members"}
                      </span>
                    ) : (
                      selectedUsers.map((u) => (
                        <span key={u.id} className="top-multiselect-chip">
                          {u.name}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleUserSelection(u.id);
                            }}
                            aria-label={`Remove ${u.name}`}
                          >
                            ✕
                          </button>
                        </span>
                      ))
                    )}
                    <span className="top-multiselect-arrow">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </div>

                  {memberDropdownOpen && (
                    <div className="top-multiselect-panel">
                      {pool.map((u) => {
                        // Checked state comes ONLY from selectedUserIds —
                        // never from u.isAssigned.
                        const selected = selectedUserIds.includes(u.id);

                        return (
                          <label
                            key={u.id}
                            className={`top-multiselect-option ${
                              selected ? "selected" : ""
                            } ${u.isAssigned ? "assigned" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              disabled={u.isAssigned}
                              onChange={() => toggleUserSelection(u.id)}
                            />

                            {u.name}

                            {u.isAssigned && (
                              <span className="top-option-tag">
                                Already grouped
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {poolError && (
                  <p className="top-inline-error">
                    {poolError}{" "}
                    <button
                      type="button"
                      className="top-inline-link-btn"
                      onClick={fetchStudents}
                    >
                      Retry
                    </button>
                  </p>
                )}
              </div>

              <div className="top-submit-row">
                <button
                  type="submit"
                  className="top-btn top-btn-primary"
                  disabled={selectedUserIds.length === 0 || creating}
                >
                  {creating ? "Creating…" : "Create group"}
                </button>
              </div>
              {createError && (
                <p className="top-inline-error" style={{ textAlign: "right" }}>
                  {createError}
                </p>
              )}
            </form>

            {/* ── Groups list ────────────────────────────────── */}
            {groupsLoading && groups.length === 0 && (
              <p className="top-empty-state">Loading groups…</p>
            )}

            {!groupsLoading && groupsError && (
              <p className="top-empty-state">
                {groupsError}{" "}
                <button
                  type="button"
                  className="top-inline-link-btn"
                  onClick={fetchGroups}
                >
                  Retry
                </button>
              </p>
            )}

            {groups.length > 0 && (
              <>
                <h2 className="top-groups-heading mb-3">
                  Groups
                  <span className="top-groups-count-pill">{groups.length}</span>
                </h2>
                <div className="top-groups">
                  {groups.map((g) => {
                    const isEditingMembers = openGroupEditId === g.id;
                    const isSavingMembers = memberSavingId === g.id;
                    const isSavingLead = leadSavingId === g.id;
                    const isDeleting = deletingId === g.id;
                    return (
                      <div
                        key={g.id}
                        className={`top-group-card ${
                          g.confirmed ? "is-confirmed" : ""
                        }`}
                      >
                        <div className="top-group-head">
                          <h3 style={{ fontWeight: "600" }}>{g.name}</h3>
                          <span
                            className={`top-status-badge ${
                              g.confirmed
                                ? "top-status-confirmed"
                                : "top-status-pending"
                            }`}
                          >
                            {g.confirmed ? "Confirmed" : "Pending"}
                          </span>
                        </div>
                        <div className="top-group-count">
                          {g.members.length} member
                          {g.members.length !== 1 ? "s" : ""}
                        </div>

                        {/* Per-group member editor — add/remove members */}
                        <div
                          className="top-group-multiselect"
                          ref={(el) => {
                            groupEditRefs.current[g.id] = el;
                          }}
                        >
                          <label className="top-label">Members</label>
                          <div className="top-multiselect">
                            <div
                              className={`top-multiselect-trigger ${
                                isEditingMembers ? "open" : ""
                              } ${g.confirmed ? "disabled" : ""}`}
                              onClick={() => {
                                if (g.confirmed) return;
                                setOpenGroupEditId((prev) =>
                                  prev === g.id ? null : g.id,
                                );
                              }}
                            >
                              {g.members.length === 0 ? (
                                <span className="top-multiselect-placeholder">
                                  No members — add some
                                </span>
                              ) : (
                                g.members.map((m) => (
                                  <span
                                    key={m.id}
                                    className="top-multiselect-chip"
                                  >
                                    {m.name}
                                    {!g.confirmed && (
                                      <button
                                        type="button"
                                        disabled={isSavingMembers}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleGroupMember(g.id, m.id);
                                        }}
                                        aria-label={`Remove ${m.name} from ${g.name}`}
                                      >
                                        ✕
                                      </button>
                                    )}
                                  </span>
                                ))
                              )}
                              {!g.confirmed && (
                                <span className="top-multiselect-arrow">
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <polyline points="6 9 12 15 18 9" />
                                  </svg>
                                </span>
                              )}
                            </div>

                            {isEditingMembers && !g.confirmed && (
                              <div className="top-multiselect-panel">
                                {pool.map((u) => {
                                  const selected = g.members.some(
                                    (m) => m.id === u.id,
                                  );
                                  // Within a SPECIFIC group's editor, a
                                  // student counts as "assigned elsewhere"
                                  // only if isAssigned is true AND they
                                  // aren't already a member of THIS group
                                  // (otherwise you could never remove them).
                                  const blocked = u.isAssigned && !selected;

                                  return (
                                    <label
                                      key={u.id}
                                      className={`top-multiselect-option ${
                                        selected ? "selected" : ""
                                      } ${blocked ? "assigned" : ""}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        disabled={isSavingMembers || blocked}
                                        onChange={() =>
                                          toggleGroupMember(g.id, u.id)
                                        }
                                      />
                                      {u.name}
                                      {blocked && (
                                        <span className="top-option-tag">
                                          Already grouped
                                        </span>
                                      )}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          {isSavingMembers && (
                            <p className="top-inline-note">Saving members…</p>
                          )}
                          {memberEditErrors[g.id] && (
                            <p className="top-inline-error">
                              {memberEditErrors[g.id]}
                            </p>
                          )}
                        </div>

                        <div className="top-lead-list">
                          {g.members.length === 0 ? (
                            <div className="top-lead-empty">
                              Add members above to choose a lead.
                            </div>
                          ) : (
                            g.members.map((m) => {
                              const selected = g.leadId === m.id;
                              return (
                                <label
                                  key={m.id}
                                  className={`top-lead-option ${
                                    selected ? "selected" : ""
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`lead-${g.id}`}
                                    checked={selected}
                                    disabled={g.confirmed || isSavingLead}
                                    onChange={() => setLead(g.id, m.id)}
                                  />
                                  <span className="top-chip-avatar">
                                    {initials(m.name)}
                                  </span>
                                  <span className="top-lead-name">
                                    {m.name}
                                  </span>
                                  {selected && (
                                    <span className="top-lead-tag">Captain</span>
                                  )}
                                </label>
                              );
                            })
                          )}
                        </div>
                        {isSavingLead && (
                          <p className="top-inline-note">Saving lead…</p>
                        )}
                        {leadErrors[g.id] && (
                          <p className="top-inline-error">{leadErrors[g.id]}</p>
                        )}

                        <div className="top-group-footer">
                          <button
                            type="button"
                            className="top-btn top-btn-danger"
                            disabled={isDeleting}
                            onClick={() => deleteGroup(g.id)}
                          >
                            {isDeleting ? "Deleting…" : "Delete"}
                          </button>
                          <div className="top-group-footer-right">
                            {g.confirmed ? (
                              <button
                                type="button"
                                className="top-btn top-btn-secondary"
                                onClick={() => editGroup(g.id)}
                              >
                                Edit
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="top-btn top-btn-primary"
                                disabled={!g.leadId}
                                onClick={() => confirmGroup(g.id)}
                              >
                                Done
                              </button>
                            )}
                          </div>
                        </div>
                        {deleteErrors[g.id] && (
                          <p className="top-inline-error">
                            {deleteErrors[g.id]}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {!groupsLoading && !groupsError && groups.length === 0 && (
              <p className="top-empty-state">
                No groups yet — create one above to get started.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
